import { WarningOutlined } from '@ant-design/icons'
import type { CommitmentKpiLabel, GoalConfigFieldsFragment } from '@salescore/client-api'
import {
  buildCommitmentKpiScheduleFormValues,
  canEditCommitmentKpiLabel,
  CommitmentKpiScheduleForm,
  type Me,
  useMe,
} from '@salescore/client-common'
import { createStartAndEndAtOptions } from '@salescore/features'
import type { CheckboxChangeEvent, FormInstance } from 'antd'
import { Checkbox, Form, Select, Space } from 'antd'
import Text from 'antd/lib/typography/Text'
import { isNotNullish } from 'effect/Predicate'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { commitmentKpiLabelPeriodSchema, type FormValue } from './UpdateGoalConfigForm'

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function CommitmentKpiLabelItemForm({
  goalConfig,
  commitmentKpiLabels,
  form,
}: {
  goalConfig: GoalConfigFieldsFragment
  commitmentKpiLabels: CommitmentKpiLabel[]
  form: FormInstance<FormValue>
}) {
  const [useCommitmentKpi, setUseCommitmentKpi] = useState(
    isNotNullish(commitmentKpiLabels) && commitmentKpiLabels.length > 0,
  )
  const { t } = useTranslation()
  const me = useMe()

  if (!showCommitmentKpiLabelItemForm(me, goalConfig)) {
    return <Form.Item name={['commitmentKpiLabel', 'action']} hidden></Form.Item>
  }

  const kpiOptions = goalConfig.kpiViews
    .filter((view) => view.achievementStatusTargetValueType !== 'zero')
    .map((view) => ({ label: view.name, value: view.id }))

  const startAndEndAtOptions = createStartAndEndAtOptions()

  return (
    <>
      <Form.Item name={['commitmentKpiLabel', 'action']} noStyle>
        <Checkbox
          className="mb-3"
          checked={useCommitmentKpi}
          onChange={(event: CheckboxChangeEvent) => {
            const { checked } = event.target
            setUseCommitmentKpi(checked)
            if (checked) {
              form.setFieldsValue({
                commitmentKpiLabel: {
                  action: 'set',
                  schedule: buildCommitmentKpiScheduleFormValues(undefined),
                },
              })
            } else {
              form.setFieldsValue({ commitmentKpiLabel: { action: 'remove' } })
            }
          }}
        >
          {t(`魂KPIを設定`)}
        </Checkbox>
      </Form.Item>
      {useCommitmentKpi && (
        <>
          <Form.Item
            name={['commitmentKpiLabel', 'viewId']}
            label={t(`KPI`)}
            rules={[{ required: true, message: t(`KPIを選択してください`) }]}
            layout="horizontal"
            className="mb-4"
          >
            <Select placeholder={t(`選択してください`)} options={kpiOptions} />
          </Form.Item>
          <Form.Item
            name={['commitmentKpiLabel', 'goalViewId']}
            label={t(`目標`)}
            layout="horizontal"
            className="mb-4"
            required
          >
            <Select placeholder={goalConfig.name} disabled value={goalConfig.id} />
          </Form.Item>
          <Form.Item
            name={['commitmentKpiLabel', 'periods']}
            label={t(`期間`)}
            rules={[{ required: true, message: t(`期間を選択してください`) }]}
            layout="horizontal"
            getValueFromEvent={(checkedValues: string[]) =>
              checkedValues.map((value) => commitmentKpiLabelPeriodSchema.parse(JSON.parse(value)))
            }
            getValueProps={(value: Array<{ startAt: string; endAt: string }>) => {
              if (!Array.isArray(value)) return { value: [] }
              return { value: value.map((v) => JSON.stringify(v)) }
            }}
            className="mb-2"
          >
            <Checkbox.Group className="mt-1 flex flex-col ">
              {startAndEndAtOptions
                .map((option) => ({
                  label: option.label,
                  value: JSON.stringify(option.value),
                }))
                .map((option) => (
                  <Checkbox key={option.label} value={option.value} className="mb-2">
                    {option.label}
                  </Checkbox>
                ))}
            </Checkbox.Group>
          </Form.Item>
          <CommitmentKpiScheduleForm fieldNamePrefix={['commitmentKpiLabel', 'schedule']} />
          <Text type="warning">
            <Space>
              <WarningOutlined />
              <span>{t(`リロードするとダッシュボードに反映されます。`)}</span>
            </Space>
          </Text>
        </>
      )}
    </>
  )
}

// 目標の時間軸が月時かつユーザー軸がユーザーまたはグループのときに組織管理者が設定可能
export function showCommitmentKpiLabelItemForm(me: Me, goalConfig: GoalConfigFieldsFragment) {
  const goalCondition =
    goalConfig.timeSpanType === 'month' && (goalConfig.userType === 'user' || goalConfig.userType === 'group')
  return goalCondition && canEditCommitmentKpiLabel(me)
}
